import { useState, useEffect } from "react";
import { T } from "../../config/theme.js";
import { api } from "../../hooks/useApi.js";
import { Card, SectionTitle, MiniChart } from "../ui/index.jsx";

export default function TrendCharts({ symbol, token, micro }) {
  const [ohlcv, setOhlcv] = useState([]);
  const [period, setPeriod] = useState("3M");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!symbol || !token) return;
    setLoading(true);
    api(`/chart/${symbol}?days=90`, {}, token)
      .then(data => { setOhlcv(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [symbol, token]);

  if (loading) return <Card style={{ marginBottom: 14, textAlign: "center", padding: 30, color: T.textMuted }}>차트 불러오는 중...</Card>;
  if (ohlcv.length < 3) return null;

  // 기간 필터
  const periods = { "1W": 5, "1M": 20, "3M": 60 };
  const days = periods[period] || 60;
  const data = ohlcv.slice(-Math.min(days, ohlcv.length));
  const closes = data.map(d => d.close);
  const volumes = data.map(d => d.volume);
  const highs = data.map(d => d.high);
  const lows = data.map(d => d.low);

  // 변화율 계산
  const calcChange = (arr, daysBack) => {
    if (arr.length < daysBack + 1) return null;
    const now = arr[arr.length - 1];
    const prev = arr[arr.length - 1 - daysBack];
    return prev !== 0 ? ((now - prev) / prev * 100) : null;
  };

  const changes = [
    { label: "3일", val: calcChange(closes, 3) },
    { label: "1주", val: calcChange(closes, 5) },
    { label: "1달", val: calcChange(closes, 20) },
    { label: "3개월", val: calcChange(closes, Math.min(60, closes.length - 1)) },
  ].filter(c => c.val !== null);

  // 거래량 평균
  const avgVol = volumes.length > 0 ? volumes.reduce((s, v) => s + v, 0) / volumes.length : 0;

  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <SectionTitle tip="실제 주가 데이터를 기반으로 기간별 변화를 보여줘요">주가 변화 추이</SectionTitle>
        <div style={{ display: "flex", gap: 4 }}>
          {Object.keys(periods).map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              style={{ padding: "4px 10px", fontSize: 11, fontWeight: period === p ? 700 : 500, background: period === p ? T.accent + "20" : "transparent", color: period === p ? T.accent : T.textMuted, border: `1px solid ${period === p ? T.accent + "40" : T.border}`, borderRadius: 6, cursor: "pointer" }}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* 기간별 변화율 */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        {changes.map((c, i) => (
          <div key={i} style={{ padding: "8px 14px", background: T.bg, borderRadius: 8, textAlign: "center", flex: "1 1 60px" }}>
            <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 600 }}>{c.label}</div>
            <div style={{ fontSize: 16, fontWeight: 900, color: c.val >= 0 ? T.green : T.red }}>
              {c.val >= 0 ? "+" : ""}{c.val.toFixed(1)}%
            </div>
          </div>
        ))}
      </div>

      {/* 주가 차트 */}
      <div style={{ marginBottom: 16 }}>
        <MiniChart data={closes.map((v, i) => ({ value: v, date: data[i]?.date?.slice(4) || "" }))} color={closes[closes.length - 1] >= closes[0] ? T.green : T.red} label="종가" height={80} />
      </div>

      {/* 거래량 바 차트 */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: T.text, fontWeight: 600, marginBottom: 6 }}>거래량</div>
        <div style={{ display: "flex", alignItems: "end", gap: 1, height: 40 }}>
          {volumes.slice(-Math.min(60, volumes.length)).map((v, i) => {
            const maxV = Math.max(...volumes);
            const h = maxV > 0 ? (v / maxV) * 40 : 0;
            const isAboveAvg = v > avgVol * 1.5;
            return <div key={i} style={{ flex: 1, height: Math.max(h, 1), background: isAboveAvg ? T.yellow : T.accent + "40", borderRadius: "2px 2px 0 0", minWidth: 1 }} />;
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: T.textMuted, marginTop: 2 }}>
          <span>{data[0]?.date?.slice(4) || ""}</span>
          <span style={{ color: T.yellow, fontSize: 9 }}>■ 평균 대비 1.5배 이상</span>
          <span>{data[data.length - 1]?.date?.slice(4) || ""}</span>
        </div>
      </div>

      {/* 52주 밴드 위치 (micro에서) */}
      {micro?.band_52w != null && (
        <div style={{ marginTop: 12, padding: "10px 14px", background: T.bg, borderRadius: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: T.text, fontWeight: 600 }}>52주 밴드 위치</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: micro.band_52w > 80 ? T.red : micro.band_52w < 20 ? T.green : T.text }}>{micro.band_52w.toFixed(1)}%</span>
          </div>
          <div style={{ position: "relative", height: 8, background: `linear-gradient(to right, ${T.green}40, ${T.accent}40, ${T.red}40)`, borderRadius: 4 }}>
            <div style={{ position: "absolute", left: `${micro.band_52w}%`, top: -3, width: 14, height: 14, borderRadius: "50%", background: T.text, border: `2px solid ${T.bg}`, transform: "translateX(-50%)" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: T.textMuted, marginTop: 3 }}>
            <span>52주 최저</span>
            <span>{micro.band_52w > 80 ? "고점 근처 — 조심" : micro.band_52w < 20 ? "저점 근처 — 기회?" : "중간 구간"}</span>
            <span>52주 최고</span>
          </div>
        </div>
      )}
    </Card>
  );
}
